/**
 * Service Initialization Hook for InboundX Chat
 *
 * This hook manages the complex initialization of all InboundX chat services including:
 * - Chat services (InboundX or Legacy N8N)
 * - Evaluation service for feedback collection
 * - Conversation tracking service
 * - Version compatibility checking
 * - Storage adapter configuration loading
 * - Chat state restoration from local storage
 *
 * It handles the entire service lifecycle from configuration loading through
 * initialization and state restoration, providing a clean interface for the
 * main chat component.
 */

import { type MutableRefObject, useCallback, useEffect, useState } from 'react';
import type { NetworkAdapter } from '../../adapters/network';
import type { StorageAdapter } from '../../adapters/storage';
import {
    registerEvaluationProvider,
    registerN8nProvider,
    registerParentWindowProvider,
    setAnalyticsContext,
} from '../../analytics';
import type { BrowserRevealData } from '../../browserReveal';
import type { EvaluationPopupResult } from '../../components/EvaluationPopup';
import { RETRY_CONFIG } from '../../config/retryConfig';
import { getParentWindowEventsConfig, isCurrentPageDemoURL } from '../../config/SiteConfigManager';
import { useConfig } from '../../config/unified/ConfigProvider';
import { useSiteConfig } from '../../context/SiteConfigContext';
import { ChatStorageService, type ConversationPair } from '../../services/chatStorage';
import { conversationScope } from '../../services/conversationScope';
// ConversationService removed - handled by new analytics system
import { createEvaluationService, type EvaluationService } from '../../services/evaluation';
import { useIdentificationAllowed } from '../../services/identificationConsent';
import type { InboundXService } from '../../services/inboundx';
import { logger } from '../../utils/core/logger';
import {
    createInitialRetryState,
    executeWithRetry,
    formatRetryDelay,
} from '../../utils/core/retryUtils';
import { resolvePageDisplayRule } from '../../utils/domain/pageDisplayRules';
import { waitForSnitcherReady } from '../useSnitcherRadar';

type ServiceType = InboundXService;

type StoredServiceConfig = Record<string, unknown> & {
    siteName?: string;
    inboundx_evaluation_enabled?: boolean;
    inboundx_related_enabled?: boolean;
};

type ServiceRuntimeConfig = StoredServiceConfig & {
    conversationLanguage?: string;
    visitorIp?: string;
    browserRevealData?: BrowserRevealData;
};

type EvaluationPopupData = {
    messageId: string;
    messageContent: string;
    rating: 'thumbs_up' | 'thumbs_down';
} | null;

interface UseServiceInitializationProps {
    domain: string;
    apiUrl: string;
    storageAdapter?: StorageAdapter;
    networkAdapter?: NetworkAdapter;
    isSiteConfigLoaded?: boolean;
    isAnalyticsReady?: boolean;
    enableEvaluation?: boolean;
    conversationLanguage?: string;
    visitorIp?: string;
    browserRevealData?: BrowserRevealData;
}

interface ServiceInitializationState {
    chatService: ServiceType | null;
    evaluationService: EvaluationService | null;
    // conversationService: removed - handled by new analytics system
    currentSessionId: string;
    // Conversation scope the transcript was restored from (see conversationScope).
    storageScope: string;
    isInitialized: boolean;
    apiMode: 'new';
    isApiCompatibleAndAvailable: boolean;
    versionCheckError: string | null;
    evaluationEnabled: boolean;
    relatedEnabled: boolean;
    conversationPairs: ConversationPair[];
    isExpanded: boolean;
    isExplicitlyMinimized: boolean;
    apiVersion?: string;
    environment?: string;
    // Retry mechanism state
    retryCount: number;
    isRetrying: boolean;
    nextRetryAt?: number;
    lastError?: string;
}

export const useServiceInitialization = ({
    domain,
    apiUrl,
    storageAdapter,
    networkAdapter,
    isSiteConfigLoaded = false,
    isAnalyticsReady = false,
    enableEvaluation = false,
    conversationLanguage,
    visitorIp,
    browserRevealData,
}: UseServiceInitializationProps) => {
    const { resolver } = useConfig();
    const {
        inboundXService,
        apiVersion: contextApiVersion,
        environment: contextEnvironment,
    } = useSiteConfig();

    const [state, setState] = useState<ServiceInitializationState>({
        chatService: null,
        evaluationService: null,
        currentSessionId: '',
        storageScope: '',
        isInitialized: false,
        apiMode: 'new',
        isApiCompatibleAndAvailable: true,
        versionCheckError: null,
        evaluationEnabled: true,
        relatedEnabled: true,
        conversationPairs: [],
        isExpanded: false,
        isExplicitlyMinimized: false,
        apiVersion: undefined,
        environment: undefined,
        ...createInitialRetryState(),
    });

    // Core initialization logic extracted for retry mechanism
    const performServiceInitialization = useCallback(
        async (_config: StoredServiceConfig) => {
            logger.debug('🔧 Initializing services with unified config');

            if (!resolver) {
                throw new Error(
                    `Config resolver not found for domain: ${domain}. Site is not supported.`
                );
            }

            const evaluationEndpoint = resolver.system.evaluation_endpoint;

            // Get service from context - it should already be created by ConfigProvider
            if (!inboundXService) {
                throw new Error(
                    'InboundXService not found in context. This is a critical bug - service should be initialized in ConfigProvider before RoseWidget renders.'
                );
            }

            logger.debug('🔧 Reusing InboundXService from context');
            const chatService: ServiceType = inboundXService;

            // IX-4552: ConfigProvider already calls /health (with retry) before it
            // dispatches READY, and puts version/environment on the context. Calling
            // getHealth() again here duplicated the request on every page load and
            // looked like polling in a network waterfall. Reuse the context values.
            const apiVersionFromService = contextApiVersion;
            const environmentFromService = contextEnvironment;

            // Create evaluation service - prioritize site-specific endpoint, fallback to analytics system
            let evaluationService: EvaluationService | null = null;

            if (evaluationEndpoint) {
                // Site-specific evaluation service (sends to N8N webhook)
                evaluationService = createEvaluationService({
                    evaluationUrl: evaluationEndpoint,
                    siteName: domain,
                    networkAdapter,
                });
                logger.debug(
                    '🎯 Created site-specific evaluation service for',
                    domain,
                    'endpoint:',
                    evaluationEndpoint
                );
            } else {
                // Fallback: Create analytics-based evaluation service
                // This service will use the Analytics.trackEvaluationSubmitted() which routes to the evaluation provider
                evaluationService = createEvaluationService({
                    evaluationUrl: 'analytics://fallback', // Special URL to indicate analytics system usage
                    siteName: domain,
                    networkAdapter,
                });
                logger.debug(
                    '🎯 Created analytics-based evaluation service for',
                    domain,
                    '(no site-specific endpoint)'
                );
            }

            // conversationService removed - handled by new analytics system
            const conversationService = null;

            // Get session ID from chat service (already generated with domain included)
            // InboundXService generates session IDs in format: session_{domain}_{timestamp}_{random}
            // This ensures consistency across all analytics (PostHog, Langfuse, etc.)
            // IX-2261: Navigation-based session reset is now handled in ConfigProvider (before analytics init)
            // to prevent race conditions where early events capture a stale session ID.
            const sessionId =
                'getSessionId' in chatService
                    ? chatService.getSessionId()
                    : `session_${domain}_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;

            logger.debug('🔑 useServiceInitialization: Final session ID:', sessionId);

            const storageScope = conversationScope(domain);

            // Load previous conversation if available
            let conversationPairs: ConversationPair[] = [];
            let isExpanded = false;
            let isExplicitlyMinimized = false;

            if (storageAdapter) {
                try {
                    const chatState = ChatStorageService.loadChatState(domain);

                    if (chatState) {
                        conversationPairs = chatState.conversationPairs;
                        isExpanded = chatState.isExpanded;
                        isExplicitlyMinimized = chatState.isExplicitlyMinimized || false;
                        logger.debug(
                            '🔧 Restored previous conversation with',
                            conversationPairs.length,
                            'conversation pairs'
                        );
                    }

                    logger.debug('🔧 Restored chat state:', {
                        isExpanded,
                        isExplicitlyMinimized,
                    });
                } catch (error) {
                    logger.warn('🔧 Failed to restore previous conversation:', error);
                }
            }

            return {
                chatService,
                evaluationService,
                conversationService,
                sessionId,
                storageScope,
                conversationPairs,
                isExpanded,
                isExplicitlyMinimized,
                apiVersionFromService,
                environmentFromService,
            };
        },
        [
            domain,
            inboundXService,
            networkAdapter,
            resolver,
            storageAdapter,
            contextApiVersion,
            contextEnvironment,
        ]
    );

    const initializeServices = useCallback(
        async (config: StoredServiceConfig) => {
            try {
                const result = await executeWithRetry(() => performServiceInitialization(config), {
                    config: RETRY_CONFIG,
                    onRetry: (attempt, error, nextDelay) => {
                        const nextRetryTime = Date.now() + nextDelay;
                        setState((prev) => ({
                            ...prev,
                            isApiCompatibleAndAvailable: false,
                            versionCheckError: `${error.message} (retrying in ${formatRetryDelay(nextDelay)}...)`,
                            retryCount: attempt + 1,
                            isRetrying: true,
                            nextRetryAt: nextRetryTime,
                            lastError: error.message,
                        }));
                    },
                    onError: (error, attempt) => {
                        logger.error(
                            `🔧 Service initialization failed after ${attempt + 1} attempts:`,
                            error
                        );
                        setState((prev) => ({
                            ...prev,
                            isApiCompatibleAndAvailable: false,
                            versionCheckError: error.message,
                            isRetrying: false,
                            nextRetryAt: undefined,
                            lastError: error.message,
                        }));
                    },
                });

                // Update state with successful initialization result
                setState((prev) => ({
                    ...prev,
                    chatService: result.chatService,
                    evaluationService: result.evaluationService,
                    currentSessionId: result.sessionId,
                    storageScope: result.storageScope,
                    isInitialized: true,
                    isApiCompatibleAndAvailable: true,
                    versionCheckError: null,
                    conversationPairs: result.conversationPairs,
                    isExpanded: result.isExpanded,
                    isExplicitlyMinimized: result.isExplicitlyMinimized,
                    apiVersion: result.apiVersionFromService,
                    environment: result.environmentFromService,
                    // Reset retry state on success
                    ...createInitialRetryState(),
                }));

                // Propagate initial sessionId, apiVersion, and environment to analytics system
                setAnalyticsContext({
                    sessionId: result.sessionId,
                    apiVersion: result.apiVersionFromService,
                    environment: result.environmentFromService,
                });

                logger.debug('🔧 Service initialization completed successfully:', {
                    sessionId: result.sessionId,
                    apiVersion: result.apiVersionFromService,
                    environment: result.environmentFromService,
                });
            } catch (error) {
                // Error handling is already done in the onError callback
                // This catch is just a safety net
                logger.error('🔧 Unexpected error in initializeServices:', error, {
                    siteName: config.siteName,
                    mode: 'new',
                });
            }
        },
        [performServiceInitialization]
    );

    const setupEvaluationPopupCallback = useCallback(
        (
            setCurrentEvaluationData: (data: EvaluationPopupData) => void,
            setShowEvaluationPopup: (show: boolean) => void,
            evaluationPopupResolveRef: MutableRefObject<
                ((result: EvaluationPopupResult) => void) | null
            >
        ) => {
            if (state.evaluationService) {
                state.evaluationService.setPopupCallback(
                    async (rating: 'thumbs_up' | 'thumbs_down') => {
                        logger.debug(
                            'EvaluationService: popup callback called with rating:',
                            rating
                        );
                        return new Promise<EvaluationPopupResult>((resolve) => {
                            if (rating === 'thumbs_up') {
                                resolve({
                                    rating: 'thumbs_up',
                                    finalRating: 'Accurate & OK',
                                    cancelled: false,
                                });
                            } else {
                                logger.debug('EvaluationService: setting up popup for thumbs_down');
                                setCurrentEvaluationData({
                                    messageId: '',
                                    messageContent: '',
                                    rating,
                                });
                                logger.debug(
                                    'EvaluationService: calling setShowEvaluationPopup(true)'
                                );
                                setShowEvaluationPopup(true);
                                evaluationPopupResolveRef.current = resolve;
                            }
                        });
                    }
                );
            }
        },
        [state.evaluationService]
    );

    // Register analytics providers when site config is loaded AND analytics is ready
    useEffect(() => {
        // Don't proceed if site config is not loaded yet
        if (!isSiteConfigLoaded) {
            logger.debug(`📋 Site configuration not loaded yet for domain: ${domain}. Waiting...`);
            return;
        }

        // Don't proceed if analytics is not initialized yet
        if (!isAnalyticsReady) {
            logger.debug(
                `📊 Analytics system not initialized yet for domain: ${domain}. Waiting...`
            );
            return;
        }

        const registerAnalyticsProviders = async () => {
            try {
                // IMPORTANT: Use the domain parameter (effectiveDomain from widget init) for API calls
                // NOT siteConfig.domain which is the database key (e.g., 'default', 'skaleet.com')
                // The domain parameter is already the correct effective domain from forceDomain or window.location
                if (!domain) {
                    logger.error('❌ Cannot register analytics providers: domain is undefined');
                    throw new Error('Domain is required for analytics provider registration');
                }

                const effectiveSiteDomain = domain;

                // Register N8N conversation provider if site has conversation endpoint
                const conversationEndpoint = resolver?.system.conversation_endpoint;
                if (conversationEndpoint) {
                    logger.info('📡 Registering N8N conversation provider:', {
                        effectiveSiteDomain,
                        conversationEndpoint,
                        widgetDomain: domain,
                        resolverDomain: resolver?.domain,
                    });
                    await registerN8nProvider({
                        conversationUrl: conversationEndpoint,
                        siteName: effectiveSiteDomain, // Use the effective domain from widget init
                        networkAdapter,
                    });
                } else {
                    logger.debug(
                        'No conversation endpoint configured for domain:',
                        effectiveSiteDomain
                    );
                }

                // Register evaluation provider if site has evaluation endpoint
                const evaluationEndpoint = resolver?.system.evaluation_endpoint;
                if (evaluationEndpoint) {
                    await registerEvaluationProvider({
                        evaluationUrl: evaluationEndpoint,
                        siteName: effectiveSiteDomain, // Use the effective domain from widget init
                    });
                }

                // Update parent window provider config if site has custom settings or explicitly disabled
                // Provider is registered during initializeAnalytics() with defaults
                const parentWindowConfig = getParentWindowEventsConfig(effectiveSiteDomain);
                if (parentWindowConfig === null) {
                    // Explicitly disabled via custom_config
                    logger.debug(
                        '🪟 Disabling parent window provider for domain:',
                        effectiveSiteDomain
                    );
                    await registerParentWindowProvider({
                        enabled: false,
                    });
                } else if (
                    parentWindowConfig.targetOrigin !== '*' ||
                    parentWindowConfig.eventCategories
                ) {
                    // Site has custom config, update the provider
                    logger.debug('🪟 Updating parent window provider config:', {
                        effectiveSiteDomain,
                        targetOrigin: parentWindowConfig.targetOrigin,
                        eventCategories: parentWindowConfig.eventCategories,
                    });
                    await registerParentWindowProvider({
                        enabled: true,
                        targetOrigin: parentWindowConfig.targetOrigin,
                        eventCategories: parentWindowConfig.eventCategories,
                    });
                }
                // Otherwise, use defaults from initializeAnalytics()
            } catch (error) {
                logger.error('Failed to register analytics providers:', error);
            }
        };

        registerAnalyticsProviders();
    }, [domain, isSiteConfigLoaded, isAnalyticsReady, networkAdapter, resolver]);

    // Load configuration from storage adapter
    useEffect(() => {
        // Don't proceed if site config is not loaded yet
        if (!isSiteConfigLoaded) {
            logger.debug(`Site configuration not loaded yet for domain: ${domain}. Waiting...`);
            return;
        }

        const loadConfig = async () => {
            logger.debug('🔧 Starting service initialization for domain:', domain);
            let config: StoredServiceConfig = {};

            if (storageAdapter) {
                try {
                    config = await storageAdapter.get([
                        'api_endpoint',
                        'inboundx_evaluation_enabled',
                        'inboundx_related_enabled',
                        'inboundx_api_key',
                    ]);
                    logger.debug('InboundXChat: Loaded config from storage:', config);
                } catch {
                    logger.warn('InboundXChat: Storage adapter not available, using defaults');
                }
            } else {
                logger.debug('🔧 No storage adapter provided, using defaults');
            }

            const mode = 'new' as const;
            const evaluationSupported = Boolean(
                resolver?.system.evaluation_endpoint || resolver?.system.api_endpoint
            );
            const evaluationEnabledInSettings = config.inboundx_evaluation_enabled !== false;

            logger.debug('🔧 Service initialization config:', {
                mode,
                evaluationSupported,
                evaluationEnabledInSettings,
                domain,
                apiUrl,
            });

            setState((prev) => ({
                ...prev,
                apiMode: mode,
                evaluationEnabled:
                    evaluationSupported && evaluationEnabledInSettings && enableEvaluation,
                relatedEnabled: config.inboundx_related_enabled !== false,
            }));

            logger.debug('🔧 About to call initializeServices...');
            initializeServices(config).catch((error) => {
                logger.error('InboundXChat: Error initializing services:', error);
                setState((prev) => ({
                    ...prev,
                    isApiCompatibleAndAvailable: false,
                    versionCheckError: 'Service initialization failed',
                }));
            });
        };

        loadConfig();
    }, [
        domain,
        apiUrl,
        storageAdapter,
        initializeServices,
        isSiteConfigLoaded,
        enableEvaluation,
        resolver,
    ]);

    // Update chatService configuration when conversationLanguage changes
    useEffect(() => {
        if (!state.chatService || !state.isInitialized) return;

        // For new API mode (InboundXService), update the service config in place
        if (state.apiMode === 'new' && 'config' in state.chatService) {
            const currentService = state.chatService as InboundXService;

            // Get current configuration
            const currentConfig = (currentService as unknown as { config?: ServiceRuntimeConfig })
                .config;

            // Update if language or visitorIp changed
            const languageChanged = currentConfig?.conversationLanguage !== conversationLanguage;
            const visitorIpChanged = currentConfig?.visitorIp !== visitorIp;

            const browserRevealChanged = currentConfig?.browserRevealData !== browserRevealData;

            if (languageChanged || visitorIpChanged || browserRevealChanged) {
                logger.debug('🌐 Service configuration changed, updating:', {
                    languageChanged,
                    visitorIpChanged,
                    browserRevealChanged,
                    oldLanguage: currentConfig?.conversationLanguage,
                    newLanguage: conversationLanguage,
                    oldVisitorIp: currentConfig?.visitorIp,
                    newVisitorIp: visitorIp,
                    hasBrowserRevealData: !!browserRevealData,
                    serviceType: state.apiMode,
                });

                // Update existing service config in place (preserves session ID)
                // IX-2261: Previously created a new InboundXService here, which could
                // generate a different session ID if the storage key differed
                currentService.updateConfig({
                    conversationLanguage,
                    visitorIp,
                    browserRevealData,
                });
            }
        }
        // Note: Legacy N8N service doesn't use conversationLanguage, visitorIp, or browserRevealData
    }, [
        conversationLanguage,
        visitorIp,
        browserRevealData,
        state.chatService,
        state.isInitialized,
        state.apiMode,
    ]);

    // Check if current page is a demo URL and minimize chat if it is
    // Exception: page_display_rules with 'displayed' override CTA minimized behavior
    useEffect(() => {
        if (!state.isInitialized) return;

        const checkDemoURL = async () => {
            if (isCurrentPageDemoURL(domain)) {
                // Check if page display rules override CTA minimized behavior
                const pageRules = resolver?.traffic_control.page_display_rules;
                const url = typeof window !== 'undefined' ? window.location.href : '';
                const matchedRule = pageRules?.length
                    ? resolvePageDisplayRule(pageRules, url)
                    : null;

                if (matchedRule?.display === 'displayed') {
                    logger.debug(
                        'InboundXChat: CTA page, but page_display_rules says "displayed" — skipping minimize'
                    );
                    return;
                }

                logger.debug(
                    'InboundXChat: Current page is a CTA destination URL, minimizing chat'
                );
                setState((prev) => ({
                    ...prev,
                    isExpanded: false,
                    isExplicitlyMinimized: true,
                }));
                ChatStorageService.setExpanded(domain, false);
            }
        };

        checkDemoURL().catch((error) => {
            logger.warn('Failed to check demo URL:', error);
        });
    }, [domain, resolver, state.isInitialized]);

    const identificationAllowed = useIdentificationAllowed(
        resolver?.qualification?.identification_requires_consent
    );

    // Track visitor impression for enrichment/person identity (fire-and-forget)
    // Runs under the same condition that loads the identification pixels in RoseWidget:
    // for everyone when enrich_all_visitors is on, otherwise only once the visitor has
    // engaged (sent >=1 message). The impression is what queues the deferred Snitcher
    // lookup, so gating it on the flag alone left engaged visitors on every other domain
    // paying for the pixel and never being identified.
    // Waits for full initialization (including PostHog analytics) before calling backend
    // so person_id is available for Redis caching
    // biome-ignore lint/correctness/useExhaustiveDependencies: domain is an intentional trigger — re-track impression when domain changes
    useEffect(() => {
        if (!state.isInitialized || !state.chatService || !state.currentSessionId) return;
        // Wait for analytics (PostHog) to be ready - ensures person_id is available for Redis caching
        if (!isAnalyticsReady) return;

        // Consent-gated sites wait for the host's consent before identifying anyone.
        if (!identificationAllowed) {
            logger.debug('🎯 Visitor impression tracking skipped - waiting for host consent');
            return;
        }
        // Identification is allowed for everyone, or only for visitors who engaged.
        const shouldIdentifyVisitor =
            resolver?.qualification?.enrich_all_visitors === true ||
            state.conversationPairs.length > 0;
        if (!shouldIdentifyVisitor) {
            logger.debug('🎯 Visitor impression tracking skipped - visitor has not engaged yet');
            return;
        }

        // Deduplicate via sessionStorage - only track once per session
        const impressionKey = `rw_impression_${state.currentSessionId}`;
        if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(impressionKey)) {
            logger.debug('🎯 Visitor impression already tracked for this session');
            return;
        }

        const chatService = state.chatService;
        const trackImpression = async () => {
            // The impression is what queues the deferred Snitcher lookup, so it is worth
            // waiting out a slow Radar boot: on the engaged-only path the pixel starts
            // loading in the same render that runs this effect, so a few seconds is a
            // cold script fetch, not a failure. Bounded — past that the load is broken.
            const snitcherSessionId = await waitForSnitcherReady(15000);

            // Only InboundXService has trackVisitorImpression
            if (chatService && 'trackVisitorImpression' in chatService) {
                await chatService.trackVisitorImpression();
                logger.debug('🎯 Visitor impression tracked successfully');
            }

            // Only close the session out once the impression could actually carry the
            // Radar session. Marking it tracked without one makes the miss permanent:
            // Snitcher becoming ready later cannot retrigger this effect. Snitcher not
            // running at all (no window.RosRd) is not a miss — waiting can never help.
            const snitcherStillPending =
                !snitcherSessionId && typeof window !== 'undefined' && Boolean(window.RosRd);
            if (snitcherStillPending) {
                logger.debug(
                    '🎯 Visitor impression sent without a Snitcher session, leaving it retriable'
                );
                return;
            }

            // Mark as tracked to prevent duplicate calls
            if (typeof sessionStorage !== 'undefined') {
                sessionStorage.setItem(impressionKey, 'true');
            }
        };

        trackImpression().catch((error) => {
            // Fire-and-forget - silently fail, don't block widget functionality
            logger.debug('🎯 Visitor impression tracking failed (non-critical):', error);
        });
    }, [
        domain,
        identificationAllowed,
        isAnalyticsReady,
        resolver,
        state.chatService,
        state.conversationPairs.length,
        state.currentSessionId,
        state.isInitialized,
    ]);

    // Cleanup effect to prevent memory leaks
    useEffect(() => {
        return () => {
            // The executeWithRetry utility handles its own cleanup internally,
            // but we ensure that any pending operations are properly cleaned up
            logger.debug('🧹 Service initialization hook cleanup - component unmounting');
        };
    }, []);

    return {
        ...state,
        setupEvaluationPopupCallback,
        setState,
    };
};
